import styled from "styled-components";
import { motion } from "framer-motion";
import { UserCheck, MapPinned, Rocket, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* ================= SECTION ================= */

const Section = styled.section`
  position: relative;
  padding: 180px 20px;
  background: radial-gradient(
      900px at 18% 0%,
      rgba(245, 158, 11, 0.11),
      transparent 55%
    ),
    radial-gradient(
      720px at 82% 24%,
      rgba(56, 189, 248, 0.12),
      transparent 55%
    ),
    ${({ theme }) => theme.colors.bg};
  overflow: hidden;
`;

/* ================= CONTAINER ================= */

const Container = styled.div`
  max-width: 1100px;
  margin: auto;
  position: relative;
`;

/* ================= HEADER ================= */

const Heading = styled(motion.h2)`
  text-align: center;
  font-size: clamp(34px, 4.5vw, 50px);
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled(motion.p)`
  text-align: center;
  max-width: 760px;
  margin: 22px auto 0;
  font-size: 18px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= STEPS ================= */

const StepsWrap = styled.div`
  position: relative;
  margin-top: 120px;
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  position: relative;
  z-index: 2;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

/* ================= CONNECTOR LINE ================= */

const Connector = styled.div`
  position: absolute;
  top: 64px;
  left: 0;
  right: 0;
  height: 2px;
  background-image: linear-gradient(
    to right,
    rgba(56, 189, 248, 0.35) 40%,
    rgba(56, 189, 248, 0) 0%
  );
  background-size: 16px 2px;
  background-repeat: repeat-x;
  animation: dash 6s linear infinite;

  @keyframes dash {
    from {
      background-position: 0 0;
    }
    to {
      background-position: 200px 0;
    }
  }

  @media (max-width: 1000px) {
    display: none;
  }
`;

/* ================= CARD ================= */

const StepCard = styled(motion.div)`
  position: relative;
  padding: 52px 42px;
  border-radius: 28px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.01)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  box-shadow: 0 30px 80px rgba(2, 6, 23, 0.65);

  ${({ emphasis }) =>
    emphasis &&
    `
    box-shadow: 0 40px 110px rgba(56,189,248,0.32);
    transform: translateY(-6px);
  `}
`;

/* ================= STEP NUMBER ================= */

const StepNumber = styled.div`
  position: absolute;
  top: -28px;
  left: 38px;
  font-size: 72px;
  font-weight: 900;
  opacity: 0.08;
  letter-spacing: -4px;
  color: ${({ theme }) => theme.colors.text};
`;

/* ================= CLOCK ================= */

const ClockWrap = styled(motion.div)`
  position: absolute;
  top: 18px;
  right: 18px;
  opacity: 0.08;
  color: ${({ theme }) => theme.colors.textMuted};

  svg {
    width: 22px;
    height: 22px;
  }
`;

/* ================= ICON ================= */

const IconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  margin-bottom: 28px;
  color: #fff;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  box-shadow: 0 0 50px rgba(56, 189, 248, 0.35);
`;

/* ================= TEXT ================= */

const Title = styled.h4`
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const Text = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const StartCTA = styled.button`
  margin-top: 60px;
  padding: 18px 38px;
  border-radius: 999px;
  align-sewlf: center;

  font-size: 17px;
  font-weight: 800;
  color: #04111f;

  display: inline-flex;

  gap: 10px;

  cursor: pointer;
  border: none;

  background: linear-gradient(135deg, #f59e0b, #facc15);
  box-shadow: 0 12px 45px rgba(245, 158, 11, 0.34);

  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-3px) scale(1.04);
    box-shadow: 0 18px 60px rgba(245, 158, 11, 0.48);
  }

  &:active {
    transform: scale(0.96);
  }
`;
const Arrow = styled.span`
  display: inline-block;
  transition: transform 0.25s ease;

  ${StartCTA}:hover & {
    transform: translateX(4px);
  }
`;
const SmallText = styled.p`
  margin-top: 14px;
  font-size: 13px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.85;
`;
const CTAWrapper = styled.div`
  margin-top: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column; /* so SmallText stays below */
`;

/* ================= ANIMATIONS ================= */

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.22 } },
};

/* ================= DATA ================= */

const STEPS = [
  {
    icon: UserCheck,
    title: "Define your direction",
    text: "Users share their level, goal, and available time so the system starts from reality.",
  },
  {
    icon: MapPinned,
    title: "Get a realistic roadmap",
    text: "The product converts career intent into a structured Phase -> Week -> Day execution plan.",
  },
  {
    icon: Rocket,
    title: "Build daily and stay accountable",
    text: "Users follow tasks, build proof-of-work, and track consistency with mentor support when needed.",
  },
];

/* ================= COMPONENT ================= */

export default function HowItWorksSection() {
  const navigate = useNavigate();
  return (
    <Section id="how-it-works-section">
      <Container>
        <Heading
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          From confusion to consistency in 3 steps
        </Heading>

        <Sub
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          Users answer a few questions.
          <br />
          We generate their roadmap.
          <br />
          They execute daily without second-guessing.
        </Sub>

        <StepsWrap>
          <Connector />

          <Steps
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-120px" }}
          >
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <StepCard
                  key={i}
                  variants={fadeUp}
                  emphasis={i === 2}
                  whileHover={{ y: -16 }}
                  transition={{ type: "spring", stiffness: 140 }}
                >
                  <StepNumber>{`0${i + 1}`}</StepNumber>

                  <ClockWrap
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 40,
                      ease: "linear",
                      repeat: Infinity,
                    }}
                  >
                    <Clock />
                  </ClockWrap>

                  <IconWrap>
                    <Icon size={30} />
                  </IconWrap>

                  <Title>{step.title}</Title>
                  <Text>{step.text}</Text>
                </StepCard>
              );
            })}
          </Steps>
        </StepsWrap>
        <CTAWrapper>
          <StartCTA onClick={() => navigate("/career-clarity-home")}>
            Create My Roadmap →
          </StartCTA>

          <SmallText>
            Free roadmap available • No credit card required
          </SmallText>
        </CTAWrapper>
      </Container>
    </Section>
  );
}
